/*
  Pip orb: a black orb with Pip's LED eyes (diamond left, heart right) looking off to the side.
  Usage: <div class="pip-orb" data-track data-state="thinking|found|unsure"></div>
    data-track   eyes follow the pointer (else they only blink and glance)
    data-state   expression; change it at any time
    data-small   avatar sizes (under ~64px): bigger, more central eyes so they still read
    data-play    he can be played with: poke him (squash + bounce), flick him sideways (he spins with
                 momentum and goes dizzy if it's a big spin). Like the gift card (Rich, 10-04).
    data-face    left | right | auto (default auto): Pip always looks toward the content.
                 auto = whichever side of his container has more room. Eyes keep diamond-left, heart-right.
  Respects prefers-reduced-motion: Pip stays still.
*/
(() => {
  const NS = 'http://www.w3.org/2000/svg';
  const D = [[0,-4],[-1,-3],[0,-3],[1,-3],[-2,-2],[-1,-2],[0,-2],[1,-2],[2,-2],[-3,-1],[-2,-1],[-1,-1],[0,-1],[1,-1],[2,-1],[3,-1],[-3,0],[-2,0],[-1,0],[0,0],[1,0],[2,0],[3,0],[-2,1],[-1,1],[0,1],[1,1],[2,1],[-1,2],[0,2],[1,2],[0,3]];
  const H = [[-2,-3],[-1,-3],[1,-3],[2,-3],[-3,-2],[-2,-2],[-1,-2],[0,-2],[1,-2],[2,-2],[3,-2],[-3,-1],[-2,-1],[-1,-1],[0,-1],[1,-1],[2,-1],[3,-1],[-3,0],[-2,0],[-1,0],[0,0],[1,0],[2,0],[3,0],[-2,1],[-1,1],[0,1],[1,1],[2,1],[-1,2],[0,2],[1,2],[0,3]];
  const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
  let n = 0;

  /* A dizzy eye: an Archimedean spiral drawn as round dots, so it stays in Pip's LED style. */
  function spiral(cx, cy) {
    let d = '';
    for (let i = 0; i <= 120; i++) {
      const t = i / 120 * Math.PI * 2 * 2.6, r = 2 + 17 * i / 120;
      d += (i ? 'L' : 'M') + (cx + Math.cos(t) * r).toFixed(1) + ' ' + (cy + Math.sin(t) * r).toFixed(1);
    }
    return `<path d="${d}"/>`;
  }

  function build(host) {
    if (host.dataset.pipBuilt) return;
    host.dataset.pipBuilt = '1';
    const id = 'pipg' + (n++);
    host.innerHTML = `<svg viewBox="0 0 200 200" role="img" aria-label="Pip">
      <defs><radialGradient id="${id}" cx="38%" cy="30%" r="75%"><stop offset="0" style="stop-color:var(--pip-1)"/><stop offset=".45" style="stop-color:var(--pip-2)"/><stop offset="1" style="stop-color:var(--pip-3)"/></radialGradient></defs>
      <g class="pip-orb__tilt"><circle cx="100" cy="100" r="92" fill="url(#${id})" style="stroke:var(--pip-rim)" stroke-width="1"/>
      <g class="pip-orb__yaw"><g class="pip-orb__eyes"><g class="pip-orb__pose"><g class="pip-orb__lids"></g><g class="pip-orb__dizzy">${spiral(72, 80)}${spiral(134, 78)}</g></g></g></g>
      <path class="pip-orb__spark" d="M150 40 l4 11 11 4 -11 4 -4 11 -4 -11 -11 -4 11 -4z"/>
      <path class="pip-orb__spark" style="animation-delay:.12s" d="M44 140 l2.5 7 7 2.5 -7 2.5 -2.5 7 -2.5 -7 -7 -2.5 7 -2.5z"/></g></svg>`;
    const lids = host.querySelector('.pip-orb__lids'), eyes = host.querySelector('.pip-orb__eyes'),
          poseG = host.querySelector('.pip-orb__pose');
    const small = host.hasAttribute('data-small');
    // The approved look is left-facing. Right-facing is its mirror image; both eye shapes are
    // left-right symmetric, so mirroring only swaps which shape sits where - we swap them back.
    const A = small ? 'translate(-4 -10) rotate(-12 100 100) translate(100 100) scale(1.0 1.2) translate(-100 -100)'
                    : 'translate(-26 -22) rotate(-14 100 100) translate(100 100) scale(.80 1) translate(-100 -100)';
    let dir = 0;                                    // -1 looks left, 1 looks right
    function dots(d) {
      lids.textContent = '';
      let i = 0;
      const place = d < 0 ? [[D, 72, 80], [H, 134, 78]] : [[H, 72, 80], [D, 134, 78]];
      for (const [cells, cx, cy] of place) for (const [x, y] of cells) {
        const c = document.createElementNS(NS, 'circle');
        c.setAttribute('cx', cx + x * 5.2); c.setAttribute('cy', cy + y * 5.2); c.setAttribute('r', 2.3);
        c.setAttribute('class', 'pip-orb__dot'); c.style.setProperty('--i', i++);
        lids.appendChild(c);
      }
      // centre of the dot cluster in its own space (what the old CSS origin used)
      const ox = 103, oy = 79;
      const left = `translate(${ox} ${oy}) ${A} translate(${-ox} ${-oy})`;
      poseG.setAttribute('transform', d < 0 ? left : `translate(200 0) scale(-1 1) ${left}`);
    }
    const face = () => {
      const want = host.getAttribute('data-face') || 'auto';
      let d = want === 'right' ? 1 : want === 'left' ? -1 : null;
      if (d === null) {
        const box = (host.closest('[data-face-in]') || host.parentElement || document.body).getBoundingClientRect();
        const r = host.getBoundingClientRect(), mid = r.left + r.width / 2;
        d = (mid - box.left) > (box.right - mid) ? -1 : 1;   // more room on the left -> look left
      }
      if (d !== dir) { dir = d; dots(d); eyes.style.transform = `translate(${dir * 3}px,-2px)`; }
    };
    face();
    addEventListener('resize', face, {passive: true});
    new MutationObserver(face).observe(host, {attributes: true, attributeFilter: ['data-face']});
    eyes.style.transform = `translate(${dir * 3}px,-2px)`;
    if (still) return;
    let busy = false;                                // while he's being played with, he stops looking around
    const look = (px, py) => {
      if (busy) return;
      const r = host.getBoundingClientRect(), dx = px - (r.left + r.width / 2), dy = py - (r.top + r.height * .45);
      const d = Math.hypot(dx, dy) || 1, k = Math.min(1, d / 500);
      eyes.style.transform = `translate(${dir * 3 + dx / d * 5 * k}px,${-2 + dy / d * 4 * k}px)`;
    };
    let last = 0;
    if (host.hasAttribute('data-track')) addEventListener('pointermove', e => { last = Date.now(); look(e.clientX, e.clientY); }, {passive: true});
    // Idle: he keeps looking around (Rich, 10-05: "more lively"). Every 0.7-2.2 s a new glance
    // in any direction; sometimes a quick double glance, sometimes a look over his shoulder
    // (the face flips to the other side, then comes back).
    const glance = (ax, ay) => { if (!busy) eyes.style.transform = `translate(${dir * 3 + ax}px,${-2 + ay}px)`; };
    const restore = (back) => { if (busy) return setTimeout(() => restore(back), 400); if (dir === back) { dots(back); glance(0, 0); } };
    (function idle() {
      setTimeout(() => {
        if (!busy && !document.hidden && Date.now() - last > 2500) {
          const roll = Math.random();
          if (roll < .16) {
            const back = dir;
            dots(-back); eyes.style.transform = `translate(${-back * 6}px,${-2 + Math.random() * 6 - 3}px)`;
            setTimeout(() => restore(back), 900 + Math.random() * 700);
          } else {
            const a = Math.random() * Math.PI * 2, m = 3 + Math.random() * 6;
            glance(Math.cos(a) * m, Math.sin(a) * m * .8);
            if (roll > .78) setTimeout(() => { const b = Math.random() * Math.PI * 2; glance(Math.cos(b) * 8, Math.sin(b) * 6); }, 280);
          }
        }
        idle();
      }, 700 + Math.random() * 1500);
    })();
    if (host.hasAttribute('data-play')) play(host, lids, () => { busy = true; }, () => { busy = false; });
    (function blink() {
      setTimeout(() => {
        lids.classList.add('is-shut');
        setTimeout(() => {
          lids.classList.remove('is-shut');
          if (Math.random() < .25) setTimeout(() => { lids.classList.add('is-shut'); setTimeout(() => lids.classList.remove('is-shut'), 110); }, 180);
          blink();
        }, 120);
      }, 3000 + Math.random() * 3000);
    })();
  }
  /* Poke and twirl. One spring for the squash, one for the hop, and a free spin
     with friction that settles facing front. The twirl is a turn about Pip's
     vertical axis (Rich: "3D spin, not like a clock"): his round body stays put
     and his face slides across, round the back and in from the other side,
     narrowing as it reaches the edge. Squash/hop sit on the svg, so the float
     animation on the host keeps working. */
  function play(host, lids, start, stop) {
    const svg = host.querySelector('svg'), yaw = host.querySelector('.pip-orb__yaw');
    host.classList.add('is-playable');
    let rot = 0, vrot = 0, sx = 1, vsx = 0, ty = 0, vty = 0;
    let down = null, dragged = false, running = false, last = 0, held = false, dizzyUntil = 0, spun = 0, settling = false;
    const draw = () => {
      svg.style.transform = `translateY(${ty.toFixed(2)}px) scale(${sx.toFixed(3)}, ${(2 - sx).toFixed(3)})`;
      const a = rot * Math.PI / 180, c = Math.cos(a), x = Math.sin(a) * 66;
      // the face is on the front half only; at the edge it is seen side-on (narrow), behind it is hidden
      yaw.setAttribute('transform', `translate(${x.toFixed(2)} 0) translate(100 100) scale(${Math.max(0.02, c).toFixed(3)} 1) translate(-100 -100)`);
      yaw.style.opacity = c > 0.05 ? Math.min(1, c * 3).toFixed(2) : 0;
    };
    const tick = (t) => {
      const dt = Math.min(0.032, (t - last) / 1000 || 0.016); last = t;
      if (!held) {
        vsx += (-300 * (sx - 1) - 9 * vsx) * dt; sx += vsx * dt;
      }
      vty += (-220 * ty - 10 * vty) * dt; ty += vty * dt;
      if (!down) {
        if (!settling && Math.abs(vrot) < 90) settling = true;   // slow enough: settle upright, never speed up again
        if (settling) {
          const goal = Math.round(rot / 360) * 360;
          vrot += (-70 * (rot - goal) - 16 * vrot) * dt;         // critically damped, so no wobble back into a spin
        } else {
          vrot *= Math.exp(-0.9 * dt);                         // a light friction: a good flick spins a lot
        }
        rot += vrot * dt; spun += Math.abs(vrot * dt);
      }
      if (spun > 720 && !host.classList.contains('is-dizzy')) host.classList.add('is-dizzy');
      if (host.classList.contains('is-dizzy')) {
        if (Math.abs(vrot) > 60) dizzyUntil = t + 1600; // stays dizzy a moment after he stops
        else if (t > dizzyUntil && dizzyUntil) { host.classList.remove('is-dizzy'); dizzyUntil = 0; spun = 0; }
      }
      draw();
      const moving = held || down || Math.abs(vrot) > 0.5 || Math.abs(rot - Math.round(rot / 360) * 360) > 0.3 ||
        Math.abs(sx - 1) > 0.002 || Math.abs(vsx) > 0.01 || Math.abs(ty) > 0.05 || Math.abs(vty) > 0.05 || host.classList.contains('is-dizzy');
      if (moving) requestAnimationFrame(tick);
      else { running = false; rot = 0; spun = 0; settling = false; svg.style.transform = ''; yaw.removeAttribute('transform'); yaw.style.opacity = ''; stop(); }
    };
    const go = () => { if (!running) { running = true; start(); last = performance.now(); requestAnimationFrame(tick); } };
    host.addEventListener('pointerdown', (e) => {
      if (e.button) return;
      down = { x: e.clientX, t: performance.now(), rot, lastX: e.clientX, lastT: performance.now(), v: 0 };
      vrot = 0; settling = false;                          // catching him stops the spin
      dragged = false; held = true; sx = 1.1; vsx = 0;        // squashed under the finger
      lids.classList.add('is-shut');
      try { host.setPointerCapture(e.pointerId); } catch (x) {}
      go();
    });
    host.addEventListener('pointermove', (e) => {
      if (!down) return;
      const now = performance.now(), dx = e.clientX - down.x;
      if (Math.abs(dx) > 6) { dragged = true; held = false; lids.classList.remove('is-shut'); }
      if (dragged) {
        rot = down.rot + dx * 0.9;                 // drag right, the face turns right
        const dt = Math.max(1, now - down.lastT);
        down.v = 0.7 * down.v + 0.3 * ((e.clientX - down.lastX) * 0.9 / dt * 1000);
        down.lastX = e.clientX; down.lastT = now;
      }
    });
    const up = () => {
      if (!down) return;
      if (dragged) { vrot = Math.max(-2600, Math.min(2600, down.v * 1.6)); spun = Math.abs(rot - down.rot); settling = false; }
      else { vsx = -2.6; vty = -260; }                       // poke: a bounce and a little hop
      down = null; held = false;
      setTimeout(() => lids.classList.remove('is-shut'), 140);
      go();
    };
    host.addEventListener('pointerup', up);
    host.addEventListener('pointercancel', up);
    host.addEventListener('lostpointercapture', up);
    // a poke or a spin never counts as a click on whatever holds him (the home tile is a link)
    host.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); });
  }

  document.querySelectorAll('.pip-orb').forEach(build);
  window.PipOrb = {build};
})();
